import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Stack,
  Button,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Tooltip,
  Alert as MuiAlert
} from '@mui/material'

import { styled } from '@mui/material/styles'
import { ReportGroupSelect } from './ReportGroupSelect'
import { ReportSelectionSelect } from './ReportSelectionSelect'
import { ReportSelectionCombined } from './ReportSelectionCombined'
import { ReportCustomization } from './ReportCustomization'
import { CardBox } from '../../theme/styleComponents'
import { SelectionField, useGenerateReportMutation } from '../../../generated'
import { Alert } from '../common/Alert'
import { ReportWidgetProps } from '../../interfaceTypes'
import { ownScopeField, pickDeclaredSelectionFields } from '../../utils/reports'
import { InfoOutlined } from '@mui/icons-material'
import { ReportDetailModal } from './ReportDetailModal'
import { useSearchParams } from 'react-router-dom'
import { AuthContext } from '../../context/AuthContext'

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#8B0000', // Dark red color
  color: 'white',
  '&:hover': {
    backgroundColor: '#6B0000'
  },
  padding: '8px 24px'
}))

export interface ReportFormat {
  value: 'xlsx' | 'csv' | 'pdf' | 'print'
  label: string
}

// Reports that run heavy per-matter calculations server-side and can take a while
// to finish. When one of these is selected we warn the user up front.
// BL5 (smallOutstandingBalances) works out the A/R balance one matter at a time, so it slows
// down as the thresholds are widened.
const LONG_RUNNING_REPORT_SELECTIONS = ['activeMattersWithNoPayments', 'smallOutstandingBalances']

export const ReportWidget: React.FC<ReportWidgetProps> = ({ reportTemplates, refetch }) => {
  const [selectedGroup, setSelectedGroup] = useState<string>('')
  const [selectedSelection, setSelectedSection] = useState<string>('')
  const [selectionFields, setSelectionFields] = useState<(SelectionField | null)[]>([])
  const [sortFields, setSortFields] = useState<string[]>([])
  const [reportFilters, setReportFilters] = useState<Record<string, any>>({})
  // Default to the classic two-dropdown flow; users can opt into the combined searchable dropdown.
  const [useCombinedMode, setUseCombinedMode] = useState<boolean>(false)
  const [, setSearchParams] = useSearchParams()

  const { currentUser } = useContext(AuthContext)

  const [openReportInfo, setOpenReportInfo] = useState(false)

  const handleCloseReportInfo = () => setOpenReportInfo(false)
  const handleOpenReportInfo = () => setOpenReportInfo(true)

  const handleMutationSuccess = (data: any, successMessage: string, refetchCallback?: () => void) => {
    const { response } = data?.generateReport || {}

    if (response?.status === 200) {
      Alert.success(successMessage)
      refetchCallback?.()
    }
  }

  const hasFormValues =
    !!selectedGroup ||
    !!selectedSelection ||
    Object.keys(reportFilters).some(k =>
      k !== 'sortFields' ? !!reportFilters[k] : (reportFilters.sortFields?.length ?? 0) > 0
    )

  const handleResetForm = () => {
    setSelectedGroup('')
  }

  const [generateReportMutation, { loading: generatingReport }] = useGenerateReportMutation({
    onError: error => {},
    onCompleted(data) {
      handleMutationSuccess(data, 'Report Requested Successfully', refetch)
    }
  })

  const validateReportGeneration = () => {
    if (!selectedGroup) {
      Alert.error('Please select a report group')
      return false
    }
    if (!selectedSelection) {
      Alert.error('Please select a report selection')
      return false
    }
    if (generatingReport) {
      return false
    }

    const requiredFields = selectionFields.filter(field => field?.required && !reportFilters[field?.field])
    if (requiredFields.length > 0) {
      Alert.error(`Please fill in all required fields: ${requiredFields.map(field => field?.label).join(', ')}`)
      return false
    }

    return true
  }

  useEffect(() => {
    // Clear the selection only when it no longer belongs to the freshly chosen group.
    // In combined mode the group + selection are set together in one action, so a matching
    // selection must be preserved here — otherwise it would be wiped before fields populate.
    const selectionBelongsToGroup = reportTemplates
      ?.find(group => group?.group === selectedGroup)
      ?.selections?.some(selection => selection.selection === selectedSelection)

    if (!selectionBelongsToGroup) {
      setSelectedSection('')
    }
    setSelectionFields([])
    setSortFields([])
    setReportFilters({})
    setSearchParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup])

  useEffect(() => {
    setSelectionFields([])
    setSortFields([])
    setReportFilters({})
    setSearchParams({}, { replace: true })
  }, [selectedSelection])

  useEffect(() => {
    if (selectedSelection) {
      const matched = reportTemplates
        ?.find(group => group?.group === selectedGroup)
        ?.selections?.find(selection => selection.selection === selectedSelection)

      const isAgingReport = matched?.label?.toLowerCase().includes('aging')

      const fields = (matched?.selectionFields || []).map(field => {
        if (field?.field === 'openDate') {
          // A template can request a rolling default window via `fromDaysAgo`
          // (e.g. RE6 = last 30 days), resolved relative to today.
          const fromDaysAgo = field.defaultValue?.fromDaysAgo
          const from = isAgingReport
            ? new Date(Date.now() - 150 * 24 * 60 * 60 * 1000)
            : fromDaysAgo != null
            ? new Date(Date.now() - fromDaysAgo * 24 * 60 * 60 * 1000)
            : new Date(field.defaultValue?.from || Date.now())
          return {
            ...field,
            defaultValue: {
              from,
              to: new Date()
            }
          }
        }
        return field
      })

      // Seed scalar defaults (e.g. an enum's default like Matter Status "Active")
      // so they are actually submitted and persisted — otherwise an untouched
      // default is only shown in the input and never stored on the report, which
      // leaves the Selection Fields column blank. Object defaults (date ranges)
      // are handled by their own pickers and intentionally skipped here.
      const initialFilters: Record<string, any> = {}
      fields.forEach(field => {
        if (field && field.defaultValue != null && typeof field.defaultValue !== 'object') {
          initialFilters[field.field] = field.defaultValue
        }
      })

      // An attorney (Resp 1) or a paralegal (Paralegal) runs these reports only for themselves. The
      // field renders read-only, so nothing would put a value in `reportFilters` otherwise and the
      // report would generate unscoped (the API rejects that, but the user would just see an error).
      const scopeField = ownScopeField(currentUser, fields)
      if (scopeField && currentUser?.id) {
        initialFilters[scopeField] = currentUser.id
      }

      setSelectionFields(fields)
      setSortFields(matched?.sortFields || [])
      setReportFilters(initialFilters)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSelection])

  const handleGenerateReport = async () => {
    if (!validateReportGeneration()) return

    // Renamed from `selectionFields` so it no longer shadows the state of the same name —
    // the declared fields are needed here to drop values left behind by a previous report.
    const { sortFields: _sortFields, ...filterValues } = reportFilters
    const declaredValues = pickDeclaredSelectionFields(filterValues, selectionFields)

    // Authoritative for scoped users: whatever is in state, the submitted Resp 1 / Paralegal is
    // their own id. The seed above keeps the form state honest; this line is what guarantees the
    // payload, independently of effect ordering or a stale filter left over from a reset.
    const scopeField = ownScopeField(currentUser, selectionFields)
    if (scopeField && currentUser?.id) {
      declaredValues[scopeField] = currentUser.id
    }

    if (declaredValues['personalPhone']) {
      declaredValues.personalPhone = { ilike: declaredValues['personalPhone'] }
    }
    if (declaredValues['phoneNumber']) {
      declaredValues.phoneNumber = { ilike: declaredValues['phoneNumber'] }
    }

    await generateReportMutation({
      variables: {
        createReportInput: {
          groupName: selectedGroup,
          selectionName: selectedSelection,
          selectionFields: { ...declaredValues },
          sortFields: _sortFields
        }
      }
    })
  }

  return (
    <CardBox mt={3}>
      <Stack spacing={3}>
        <Box
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid rgba(0, 0, 0, 0.12)"
        >
          <Typography variant="body1" fontSize="24px">
            Reports
          </Typography>

          <Box display={'flex'} gap={1} alignItems={'center'}>
            <IconButton title="Report Details" color="primary" onClick={handleOpenReportInfo}>
              <InfoOutlined />
            </IconButton>

            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="outlined" onClick={handleResetForm} disabled={!hasFormValues}>
                RESET
              </Button>

              <StyledButton variant="contained" onClick={handleGenerateReport}>
                GENERATE REPORT
              </StyledButton>
            </Stack>
          </Box>
        </Box>

        <Box px={2} pt={2} pb={0} style={{ margin: 0 }}>
          <Tooltip title="Switch between the classic group + report dropdowns and a single searchable dropdown that lists all reports grouped by their financial group.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={useCombinedMode}
                  onChange={event => setUseCombinedMode(event.target.checked)}
                />
              }
              label="Combined selection"
            />
          </Tooltip>
        </Box>

        {useCombinedMode ? (
          <Stack direction="row" spacing={2} p={2} pt={3} style={{ margin: 0 }}>
            <Box sx={{ width: '50%' }}>
              <ReportSelectionCombined
                reportTemplates={reportTemplates}
                selectedGroup={selectedGroup}
                selectedSelection={selectedSelection}
                onChange={(group, selection) => {
                  setSelectedGroup(group)
                  setSelectedSection(selection)
                }}
              />
            </Box>
          </Stack>
        ) : (
          <Stack direction="row" spacing={2} p={2} pt={3} style={{ margin: 0 }}>
            <Box sx={{ flex: 1 }}>
              <ReportGroupSelect
                reportTemplates={reportTemplates}
                value={selectedGroup}
                onChange={value => setSelectedGroup(value)}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <ReportSelectionSelect
                reportTemplates={reportTemplates}
                value={selectedSelection}
                onChange={value => setSelectedSection(value)}
                groupId={selectedGroup}
              />
            </Box>
          </Stack>
        )}

        {LONG_RUNNING_REPORT_SELECTIONS.includes(selectedSelection) && (
          <Box px={2} pt={2} pb={0} style={{ margin: 0 }}>
            <MuiAlert severity="info">
              This report involves a lot of calculations, so it may take a while to generate. You will be notified once
              it's ready.
            </MuiAlert>
          </Box>
        )}

        {!!selectionFields?.length && (
          // Keyed on the selection so switching reports REMOUNTS every filter.
          // Filter components keep their own internal state (DateRangePicker's
          // tempDateRange, Autocomplete's displayed option) and are keyed by
          // field name, so two reports sharing a field — e.g. PR4 and PR4A both
          // have `workDate` and `user` — reused the same instance. The parent
          // cleared reportFilters but the inputs went on showing the previous
          // values, so the report generated unfiltered while the UI claimed
          // otherwise. Remounting guarantees they re-initialise together.
          <ReportCustomization
            key={selectedSelection}
            reportSelection={selectedSelection}
            reportFilters={reportFilters}
            setReportFilters={setReportFilters}
            selectionFields={selectionFields}
            sortFields={sortFields}
          />
        )}

        <ReportDetailModal
          isOpen={openReportInfo}
          handleClose={handleCloseReportInfo}
          reportTemplates={reportTemplates}
        />
      </Stack>
    </CardBox>
  )
}
